import { useCallback, useEffect, useMemo, useState } from 'react';
import { events as seedEvents } from '../data/events';
import { ME_PERFORMER_ID, performers as seedPerformers } from '../data/performers';
import {
  cannedReplies,
  matches as seedMatches,
  messages as seedMessages,
  offers as seedOffers,
  organiserPrefs as seedOrganiserPrefs,
  performerPrefs as seedPerformerPrefs,
} from '../data/conversations';
import { defaultFilters } from '../utils/event';
import type {
  ChatMessage,
  EventFilters,
  Match,
  MusicEvent,
  Offer,
  OrganiserPrefs,
  Performer,
  PerformerPrefs,
  Role,
} from '../types/marketplace';

export const FREE_APPLY_LIMIT = 10;

const uid = () => Math.random().toString(36).slice(2, 10);
const nowIso = () => new Date().toISOString();
const matchId = (eventId: string, performerId: string) => `m-${eventId}-${performerId}`;

export type ApplyResult = 'applied' | 'matched' | 'limit';

export function useMarketplaceState(initialRole: Role) {
  const [role, setRole] = useState<Role>(initialRole);
  const [events, setEvents] = useState<MusicEvent[]>(seedEvents);
  const [performers, setPerformers] = useState<Performer[]>(seedPerformers);
  const [matches, setMatches] = useState<Match[]>(seedMatches);
  const [messages, setMessages] = useState<Record<string, ChatMessage[]>>(seedMessages);
  const [offers, setOffers] = useState<Offer[]>(seedOffers);
  const [applied, setApplied] = useState<string[]>(['e5', 'e3', 'e7']);
  const [saved, setSaved] = useState<string[]>(['e9']);
  const [passedEvents, setPassedEvents] = useState<string[]>([]);
  const [invites, setInvites] = useState<string[]>(['e6']);
  const [declinedInvites, setDeclinedInvites] = useState<string[]>([]);
  const [orgPassed, setOrgPassed] = useState<string[]>([]);
  const [orgInvited, setOrgInvited] = useState<string[]>([]);
  const [isPro, setPro] = useState(false);
  const [appliesUsed, setAppliesUsed] = useState(7);
  const [celebration, setCelebration] = useState<Match | null>(null);
  const [filters, setFilters] = useState<EventFilters>(defaultFilters);
  const [performerPrefs, setPerformerPrefs] = useState<PerformerPrefs>(seedPerformerPrefs);
  const [organiserPrefs, setOrganiserPrefs] = useState<OrganiserPrefs>(seedOrganiserPrefs);
  const [typing, setTyping] = useState<Record<string, boolean>>({});

  useEffect(() => setRole(initialRole), [initialRole]);

  const me = performers.find((p) => p.id === ME_PERFORMER_ID) as Performer;
  const getEvent = useCallback((id: string) => events.find((e) => e.id === id), [events]);
  const getPerformer = useCallback((id: string) => performers.find((p) => p.id === id), [performers]);
  const hasMatch = useCallback(
    (eventId: string, performerId: string) => matches.some((m) => m.id === matchId(eventId, performerId)),
    [matches],
  );

  const appendMessage = (id: string, msg: Omit<ChatMessage, 'id' | 'at'>) =>
    setMessages((prev) => ({ ...prev, [id]: [...(prev[id] ?? []), { ...msg, id: uid(), at: nowIso() }] }));

  const createMatch = (eventId: string, performerId: string, celebrate = true) => {
    const id = matchId(eventId, performerId);
    const match: Match = { id, eventId, performerId, createdAt: nowIso() };
    setMatches((prev) => (prev.some((m) => m.id === id) ? prev : [match, ...prev]));
    const ev = events.find((e) => e.id === eventId);
    setMessages((prev) =>
      prev[id]
        ? prev
        : { ...prev, [id]: [{ id: uid(), from: 'system', kind: 'system', text: `You matched on ${ev?.name ?? 'this event'}.`, at: nowIso() }] },
    );
    if (celebrate) setCelebration(match);
    return match;
  };

  // Performer actions
  const applyToEvent = (eventId: string, note?: string): ApplyResult => {
    if (!isPro && appliesUsed >= FREE_APPLY_LIMIT) return 'limit';
    setApplied((prev) => (prev.includes(eventId) ? prev : [...prev, eventId]));
    setAppliesUsed((n) => n + 1);
    setEvents((prev) =>
      prev.map((e) =>
        e.id === eventId && !e.applicantIds.includes(ME_PERFORMER_ID)
          ? { ...e, applicantIds: [...e.applicantIds, ME_PERFORMER_ID] }
          : e,
      ),
    );
    const ev = events.find((e) => e.id === eventId);
    if (ev?.likesYou || invites.includes(eventId)) {
      setInvites((prev) => prev.filter((i) => i !== eventId));
      const m = createMatch(eventId, ME_PERFORMER_ID);
      if (note?.trim()) appendMessage(m.id, { from: 'performer', kind: 'text', text: note.trim() });
      return 'matched';
    }
    return 'applied';
  };

  const passEvent = (eventId: string) => setPassedEvents((prev) => [...prev, eventId]);
  const undoPass = () => setPassedEvents((prev) => prev.slice(0, -1));
  const toggleSave = (eventId: string) => {
    const willSave = !saved.includes(eventId);
    setSaved((prev) => (willSave ? [...prev, eventId] : prev.filter((s) => s !== eventId)));
    return willSave;
  };

  const visibleInvites = useMemo(() => {
    const extra = isPro
      ? events.filter((e) => e.likesYou && !applied.includes(e.id)).map((e) => e.id)
      : [];
    return [...invites, ...extra].filter(
      (id, i, arr) => arr.indexOf(id) === i && !declinedInvites.includes(id) && !hasMatch(id, ME_PERFORMER_ID),
    );
  }, [invites, isPro, events, applied, declinedInvites, hasMatch]);

  const hiddenInviteCount = isPro
    ? 0
    : events.filter((e) => e.likesYou && !applied.includes(e.id) && !hasMatch(e.id, ME_PERFORMER_ID)).length;

  const acceptInvite = (eventId: string) => {
    setInvites((prev) => prev.filter((i) => i !== eventId));
    setApplied((prev) => (prev.includes(eventId) ? prev : [...prev, eventId]));
    createMatch(eventId, ME_PERFORMER_ID);
  };
  const declineInvite = (eventId: string) => setDeclinedInvites((prev) => [...prev, eventId]);

  // Organiser actions
  const likeApplicant = (eventId: string, performerId: string) => createMatch(eventId, performerId);
  const invitePerformer = (eventId: string, performerId: string) => {
    setOrgInvited((prev) => [...prev, `${eventId}:${performerId}`]);
    const p = performers.find((x) => x.id === performerId);
    if (p?.responsive) setTimeout(() => createMatch(eventId, performerId), 1400);
    return Boolean(p?.responsive);
  };
  const passPerformer = (eventId: string, performerId: string) =>
    setOrgPassed((prev) => [...prev, `${eventId}:${performerId}`]);
  const undoOrgPass = () => setOrgPassed((prev) => prev.slice(0, -1));

  // Chat
  const sendMessage = (id: string, from: Role, payload: { text?: string; songId?: string }) => {
    appendMessage(id, { from, kind: payload.songId ? 'song' : 'text', ...payload });
    const other: Role = from === 'performer' ? 'organiser' : 'performer';
    setTimeout(() => setTyping((t) => ({ ...t, [id]: true })), 500);
    setTimeout(() => {
      setTyping((t) => ({ ...t, [id]: false }));
      const pool = cannedReplies[other];
      appendMessage(id, { from: other, kind: 'text', text: pool[Math.floor(Math.random() * pool.length)] });
    }, 2100);
  };

  const sendOffer = (id: string, data: Omit<Offer, 'id' | 'matchId' | 'status'>) => {
    const offer: Offer = { ...data, id: uid(), matchId: id, status: 'pending' };
    setOffers((prev) => [...prev, offer]);
    appendMessage(id, { from: 'organiser', kind: 'offer', offerId: offer.id });
    const match = matches.find((m) => m.id === id);
    if (match) pencilSlot(match.eventId, data.slotId, match.performerId);
  };

  const respondOffer = (offerId: string, accept: boolean) => {
    const offer = offers.find((o) => o.id === offerId);
    if (!offer) return;
    setOffers((prev) => prev.map((o) => (o.id === offerId ? { ...o, status: accept ? 'accepted' : 'declined' } : o)));
    const match = matches.find((m) => m.id === offer.matchId);
    if (accept && match) {
      setEvents((prev) =>
        prev.map((e) =>
          e.id === match.eventId
            ? { ...e, slots: e.slots.map((s) => (s.id === offer.slotId ? { ...s, filledBy: match.performerId, pencilled: undefined } : s)) }
            : e,
        ),
      );
      appendMessage(offer.matchId, { from: 'system', kind: 'confirmed', offerId });
    } else {
      appendMessage(offer.matchId, { from: 'system', kind: 'system', text: 'Offer declined. You can keep chatting or send a new offer.' });
    }
  };

  const pencilSlot = (eventId: string, slotId: string, performerId?: string) =>
    setEvents((prev) =>
      prev.map((e) =>
        e.id === eventId
          ? {
              ...e,
              slots: e.slots.map((s) =>
                s.id === slotId
                  ? { ...s, pencilled: performerId }
                  : performerId && s.pencilled === performerId
                    ? { ...s, pencilled: undefined }
                    : s,
              ),
            }
          : e,
      ),
    );

  const createEvent = (event: MusicEvent) => setEvents((prev) => [event, ...prev]);
  const featureEvent = (eventId: string) =>
    setEvents((prev) => prev.map((e) => (e.id === eventId ? { ...e, featured: true } : e)));
  const updateMe = (patch: Partial<Performer>) =>
    setPerformers((prev) => prev.map((p) => (p.id === ME_PERFORMER_ID ? { ...p, ...patch } : p)));

  return {
    role,
    setRole,
    me,
    events,
    performers,
    matches,
    messages,
    offers,
    applied,
    saved,
    passedEvents,
    visibleInvites,
    hiddenInviteCount,
    orgPassed,
    orgInvited,
    isPro,
    setPro,
    appliesUsed,
    celebration,
    setCelebration,
    filters,
    setFilters,
    performerPrefs,
    setPerformerPrefs,
    organiserPrefs,
    setOrganiserPrefs,
    typing,
    getEvent,
    getPerformer,
    hasMatch,
    applyToEvent,
    passEvent,
    undoPass,
    toggleSave,
    acceptInvite,
    declineInvite,
    likeApplicant,
    invitePerformer,
    passPerformer,
    undoOrgPass,
    sendMessage,
    sendOffer,
    respondOffer,
    pencilSlot,
    createEvent,
    featureEvent,
    updateMe,
  };
}

export type MarketplaceState = ReturnType<typeof useMarketplaceState>;
